import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import LevelShield from '../components/LevelShield';
import Avatar from '../components/Avatar';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function Me() {
  const { apiFetch, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [pastChallenges, setPastChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Push notification state
  const [pushState, setPushState] = useState('loading'); // loading | unsupported | denied | subscribed | unsubscribed
  const [pushLoading, setPushLoading] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);

  // Account settings state (only used when self_manage is true)
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  const showPush = 'serviceWorker' in navigator && 'PushManager' in window;

  const checkPushStatus = useCallback(async () => {
    if (!showPush) {
      setPushState('unsupported');
      return;
    }
    try {
      // Fetch VAPID key
      const data = await apiFetch('/api/push/vapid-public-key');
      setVapidKey(data.publicKey);

      // Check current permission
      if (Notification.permission === 'denied') {
        setPushState('denied');
        return;
      }

      // Check if already subscribed
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setPushState(sub ? 'subscribed' : 'unsubscribed');
    } catch {
      setPushState('unsupported');
    }
  }, [showPush, apiFetch]);

  useEffect(() => {
    Promise.allSettled([
      apiFetch('/api/me/stats'),
      apiFetch('/api/me/badges'),
      apiFetch('/api/seasons/past'),
    ])
      .then(([statsResult, badgesResult, challengesResult]) => {
        if (statsResult.status === 'fulfilled') setStats(statsResult.value);
        if (badgesResult.status === 'fulfilled') setBadges(badgesResult.value.badges || []);
        if (challengesResult.status === 'fulfilled') setPastChallenges(challengesResult.value.challenges || []);
        if (statsResult.status === 'rejected' && badgesResult.status === 'rejected' && challengesResult.status === 'rejected') {
          setError('Failed to load profile data. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [apiFetch]);

  useEffect(() => {
    if (showPush) checkPushStatus();
  }, [showPush, checkPushStatus]);

  const handleEnablePush = async () => {
    if (!vapidKey) return;
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        setPushState('denied');
        return;
      }
      if (permission !== 'granted') return;

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      await apiFetch('/api/push/subscribe', {
        method: 'POST',
        body: JSON.stringify(sub.toJSON()),
      });

      setPushState('subscribed');
    } catch (err) {
      alert(err.message || 'Failed to enable notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await apiFetch('/api/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setPushState('unsubscribed');
    } catch (err) {
      alert(err.message || 'Failed to disable notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { setNameMsg('First and last name are required.'); return; }
    setNameSaving(true);
    setNameMsg('');
    try {
      await apiFetch('/api/player/profile', {
        method: 'PUT',
        body: JSON.stringify({ first_name: firstName.trim(), last_name: lastName.trim() }),
      });
      setNameMsg('Name updated.');
    } catch (err) {
      setNameMsg(err.message || 'Failed to update name.');
    } finally {
      setNameSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) { setPwMsg('All fields are required.'); return; }
    if (newPassword.length < 6) { setPwMsg('New password must be at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setPwMsg('Passwords do not match.'); return; }
    setPwSaving(true);
    setPwMsg('');
    try {
      await apiFetch('/api/player/password', {
        method: 'PUT',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      setPwMsg('Password changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwMsg(err.message || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return <div className="page"><div className="loading"><div className="spinner" /></div></div>;
  }

  if (error && !stats) {
    return (
      <div className="page">
        <h1 className="page-title">My Profile</h1>
        <div className="no-season-msg" style={{ color: 'var(--danger, #e74c3c)' }}>{error}</div>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn btn-sm btn-outline" onClick={() => { setError(null); setLoading(true); Promise.allSettled([apiFetch('/api/me/stats'), apiFetch('/api/me/badges'), apiFetch('/api/seasons/past')]).then(([s, b, c]) => { if (s.status === 'fulfilled') setStats(s.value); if (b.status === 'fulfilled') setBadges(b.value.badges || []); if (c.status === 'fulfilled') setPastChallenges(c.value.challenges || []); if (s.status === 'rejected' && b.status === 'rejected' && c.status === 'rejected') setError('Failed to load profile data. Please try again.'); }).finally(() => setLoading(false)); }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const level = stats?.level;
  const earnedBadges = badges.filter(b => b.earned_at);
  const latestBadge = earnedBadges.length > 0
    ? earnedBadges.reduce((a, b) => new Date(a.earned_at) > new Date(b.earned_at) ? a : b)
    : null;

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <Avatar firstName={user.first_name} lastName={user.last_name} level={level} latestBadgeEmoji={latestBadge?.icon_emoji} size={56} />
        <h1 className="page-title" style={{ marginBottom: 0 }}>
          {user.first_name} {user.last_name}
        </h1>
      </div>

      {level && (
        <div className="level-section">
          <LevelShield name={level.name} color={level.color} textColor={level.textColor} isPrestige={level.isPrestige} subtitle={level.subtitle} size="large" />
          <div className="level-info">
            <div className="level-label">Level</div>
            <div className="level-name">{level.name}</div>
            {level.nextLevelName && (
              <div className="level-next">Next: {level.nextLevelName}</div>
            )}
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats?.current_streak || 0}</div>
          <div className="stat-label">Current Streak</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.longest_streak || 0}</div>
          <div className="stat-label">Longest Streak</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.total_completions || 0}</div>
          <div className="stat-label">Completions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.total_points || 0}</div>
          <div className="stat-label">Challenge Points</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 12, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2 }}>Career Total</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--orange)' }}>{stats?.lifetime_points || 0}</div>
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: 180, textAlign: 'right' }}>
          All the points you've earned across every challenge
        </div>
      </div>

      <h2 className="page-title">Badges</h2>
      <div className="badge-shelf">
        {badges.map((b) => (
          <div key={b.id} className={`badge-item ${b.earned_at ? 'earned' : 'unearned'}`}>
            <div className="badge-emoji">{b.icon_emoji}</div>
            <div className="badge-name">{b.name}</div>
            <div className="badge-desc">{b.description}</div>
          </div>
        ))}
      </div>

      {showPush && pushState !== 'loading' && pushState !== 'unsupported' && (
        <div className="card" style={{ marginTop: 16, padding: '14px 16px' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Notifications</div>
          {pushState === 'denied' && (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Notifications are blocked. Open your browser or device settings to allow notifications for this app.
            </div>
          )}
          {pushState === 'unsubscribed' && (
            <button
              className="btn btn-orange"
              onClick={handleEnablePush}
              disabled={pushLoading}
              style={{ width: '100%' }}
            >
              {pushLoading ? 'Enabling...' : 'Enable Notifications'}
            </button>
          )}
          {pushState === 'subscribed' && (
            <div>
              <div style={{ fontSize: '0.85rem', color: '#4caf50', marginBottom: 8 }}>
                Notifications are enabled on this device.
              </div>
              <button
                className="btn btn-outline btn-sm"
                onClick={handleDisablePush}
                disabled={pushLoading}
              >
                {pushLoading ? 'Disabling...' : 'Disable Notifications'}
              </button>
            </div>
          )}
        </div>
      )}

      {pastChallenges.length > 0 && (
        <>
          <h2 className="page-title" style={{ marginTop: 24 }}>Past Challenges</h2>
          {pastChallenges.map((s) => (
            <div key={s.id} className="card" style={{ marginBottom: 10, padding: '12px 16px' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                {new Date(s.start_date?.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' — '}
                {new Date(s.end_date?.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
              {s.my_stats ? (
                <div style={{ display: 'flex', gap: 16, fontSize: '0.85rem' }}>
                  <div><span style={{ fontWeight: 700, color: 'var(--orange)' }}>{s.my_stats.season_points}</span> pts</div>
                  <div>Best streak: <span style={{ fontWeight: 600 }}>{s.my_stats.longest_streak}</span></div>
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No stats recorded</div>
              )}
            </div>
          ))}
        </>
      )}

      {user?.self_manage && (
        <>
          <h2 className="page-title" style={{ marginTop: 24 }}>Account Settings</h2>

          <div className="card" style={{ padding: '16px', marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Your Name</div>
            <form onSubmit={handleSaveName}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  className="input"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={{ flex: 1, padding: '10px 12px' }}
                />
                <input
                  className="input"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={{ flex: 1, padding: '10px 12px' }}
                />
              </div>
              {nameMsg && <div style={{ fontSize: '0.8rem', color: nameMsg === 'Name updated.' ? '#4caf50' : 'var(--danger)', marginBottom: 8 }}>{nameMsg}</div>}
              <button className="btn btn-orange" type="submit" disabled={nameSaving} style={{ width: '60%' }}>
                {nameSaving ? 'Saving...' : 'Save Name'}
              </button>
            </form>
          </div>

          <div className="card" style={{ padding: '16px' }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Change Password</div>
            <form onSubmit={handleChangePassword}>
              <input
                className="input"
                type="password"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: 8 }}
              />
              <input
                className="input"
                type="password"
                placeholder="New password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: 8 }}
              />
              <input
                className="input"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: 8 }}
              />
              {pwMsg && <div style={{ fontSize: '0.8rem', color: pwMsg === 'Password changed.' ? '#4caf50' : 'var(--danger)', marginBottom: 8 }}>{pwMsg}</div>}
              <button className="btn btn-orange" type="submit" disabled={pwSaving} style={{ width: '60%' }}>
                {pwSaving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
